import React from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { useScrollReveal } from '../hooks/useScrollReveal';
import { Building2, Wallet, Check } from 'lucide-react';

export default function BenefitsSection() {
  const { t } = useLanguage();
  const ref = useScrollReveal();

  const ceoBenefits = [
    t('benefits.ceo_benefit1'),
    t('benefits.ceo_benefit2'),
    t('benefits.ceo_benefit3'),
    t('benefits.ceo_benefit4'),
    t('benefits.ceo_benefit5'),
  ];

  const investorBenefits = [
    t('benefits.investor_benefit1'),
    t('benefits.investor_benefit2'),
    t('benefits.investor_benefit3'),
    t('benefits.investor_benefit4'),
    t('benefits.investor_benefit5'),
  ];

  return (
    <section id="benefits" data-testid="benefits-section" className="py-24 md:py-32 relative">
      <div className="absolute inset-0 bg-gradient-to-b from-[#0B1E2E] via-[#0E2538] to-[#0B1E2E]" />

      <div ref={ref} className="reveal relative z-10 max-w-7xl mx-auto px-6 md:px-12">
        <p className="text-sm uppercase tracking-widest text-[#42D2D9] font-bold mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
          {t('benefits.caption')}
        </p>
        <div className="section-line mb-8" />

        <h2 className="text-3xl sm:text-4xl md:text-5xl font-semibold tracking-tight text-white mb-16 max-w-3xl" style={{ fontFamily: 'Outfit, sans-serif' }}>
          {t('benefits.title')}
        </h2>

        <div className="grid lg:grid-cols-2 gap-8">
          {/* CEOs */}
          <div data-testid="benefits-ceo" className="glass-card p-8 md:p-10 rounded-lg">
            <div className="flex items-center gap-4 mb-8">
              <div className="w-12 h-12 rounded-lg bg-[#42D2D9]/10 flex items-center justify-center">
                <Building2 className="w-6 h-6 text-[#42D2D9]" strokeWidth={1.5} />
              </div>
              <h3 className="text-2xl font-semibold text-white" style={{ fontFamily: 'Outfit, sans-serif' }}>
                {t('benefits.ceo_title')}
              </h3>
            </div>
            <ul className="space-y-5">
              {ceoBenefits.map((benefit, i) => (
                <li key={i} className="flex items-start gap-3">
                  <div className="w-5 h-5 rounded-full bg-[#42D2D9]/10 flex items-center justify-center shrink-0 mt-0.5">
                    <Check className="w-3 h-3 text-[#42D2D9]" />
                  </div>
                  <span className="text-sm text-slate-300 leading-relaxed">{benefit}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* Investors */}
          <div data-testid="benefits-investor" className="glass-card p-8 md:p-10 rounded-lg">
            <div className="flex items-center gap-4 mb-8">
              <div className="w-12 h-12 rounded-lg bg-[#42D2D9]/10 flex items-center justify-center">
                <Wallet className="w-6 h-6 text-[#42D2D9]" strokeWidth={1.5} />
              </div>
              <h3 className="text-2xl font-semibold text-white" style={{ fontFamily: 'Outfit, sans-serif' }}>
                {t('benefits.investor_title')}
              </h3>
            </div>
            <ul className="space-y-5">
              {investorBenefits.map((benefit, i) => (
                <li key={i} className="flex items-start gap-3">
                  <div className="w-5 h-5 rounded-full bg-[#42D2D9]/10 flex items-center justify-center shrink-0 mt-0.5">
                    <Check className="w-3 h-3 text-[#42D2D9]" />
                  </div>
                  <span className="text-sm text-slate-300 leading-relaxed">{benefit}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </section>
  );
}
